import { Button, cn } from "@beweegbaas/ui";
import { createFileRoute, Link, useRouterState } from "@tanstack/react-router";
import { motion, type HTMLMotionProps } from "motion/react";
import type { ComponentProps, CSSProperties } from "react";
import { useMemo, useRef } from "react";
import { useSpinDelay } from "spin-delay";
import { tv } from "tailwind-variants";
import {
  AVATAR_INSET,
  GROUP_GAP,
  NAME_AVATAR_GAP,
  NAME_LINE_HEIGHT,
  NAME_PADDING,
  type NamePlan,
  planNames,
  usePageRoom,
} from "#/modules/les.leerling-gestuurd/fit-name-grid";
import { useLeerlingGestuurdeLes } from "#/modules/les.leerling-gestuurd/use-leerling-gestuurde-les";
import type { components } from "#/schema";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/lesson/$lesson/student/",
)({
  component: StudentIndexPage,
});

type StudentLessonParams = {
  locale: string;
  schoolyear?: string;
  school: string;
  lesson: string;
};

interface StudentProps extends Omit<ComponentProps<typeof Button>, "render" | "nativeButton"> {
  leerling: components["schemas"]["LesgroepLeerlingModel"];
  params: StudentLessonParams;
  groupIndex: number;
}

// Render a single student; the page sizes the name and avatar of every button (`--name-font`,
// `--avatar-size`).
function Student({ leerling, params, className, groupIndex: _groupIndex, ...props }: StudentProps) {
  const { getStudentLinkOptions, lesson } = useLeerlingGestuurdeLes();
  const showAnimal = lesson.avatarsEnabled;
  const navigation = useRouterState({
    select: (state) => ({
      status: state.status,
      pathname: state.location.pathname,
    }),
  });

  const avatarStyle = { width: "var(--avatar-size)", right: AVATAR_INSET };
  const studentIsPending =
    navigation.status === "pending" && navigation.pathname.includes(leerling.id);
  const isPending = useSpinDelay(studentIsPending);

  return (
    <Button
      variant="reset"
      className={cn(
        "relative inline-flex size-full max-h-[120px] items-center justify-center py-0",
        // Names wrap only on spaces and hyphens, as the page's font assumes (so no hyphens-auto,
        // which splits "Jesse" into "Jes-se"); Button itself does not wrap.
        "text-center text-balance break-words whitespace-normal no-underline",
        "font-heading font-black text-inherit",
        // Without corner-shape (Safari) rounded-full is a pill; a smaller radius looks like the
        // squircle instead.
        "clicky-button-shadow overflow-hidden rounded-[28px] supports-squircle:rounded-full",
        `bg-linear-to-b from-white/80 to-white/60 hover:from-white/90
        hover:to-white/70`,
        className,
      )}
      disabled={isPending}
      render={<Link {...getStudentLinkOptions({ ...params, studentId: leerling.id })} />}
      style={{
        fontSize: "var(--name-font)",
        lineHeight: NAME_LINE_HEIGHT,
        paddingLeft: NAME_PADDING,
        paddingRight: showAnimal
          ? `calc(var(--avatar-size) + ${AVATAR_INSET + NAME_AVATAR_GAP}px)`
          : NAME_PADDING,
      }}
      {...props}
    >
      {leerling.name}
      {showAnimal && (
        <>
          <img
            alt=""
            src={`/avatars/animals-5/${leerling.avatarNummer}.svg`}
            style={avatarStyle}
            className="absolute bottom-1/2 h-auto max-h-full shrink-0 translate-y-1/2
              object-contain opacity-50 blur brightness-50"
          />
          <img
            alt=""
            src={`/avatars/animals-5/${leerling.avatarNummer}.svg`}
            style={avatarStyle}
            className="absolute bottom-1/2 h-auto max-h-full shrink-0 translate-y-1/2
              object-contain"
          />
        </>
      )}
    </Button>
  );
}

interface LesGroepProps extends HTMLMotionProps<"article"> {
  lesgroep: components["schemas"]["LesgroepModel"];
  params: StudentLessonParams;
  index: number;
  /** Null until the page has measured its room. */
  plan: NamePlan | null;
}

// LesGroep variants using the base color system
const LesGroepVariants = tv({
  base: ["rounded-lg p-6"],
  variants: {
    color: {
      blue: "bg-gradient-to-br text-blue-900 bg-linear-to-tl to-blue-500 from-blue-400",
      green: "bg-gradient-to-br text-green-900 bg-linear-to-tl to-green-500 from-green-400",
      red: "bg-gradient-to-br text-red-900 bg-linear-to-tl to-red-500 from-red-400",
      yellow: "bg-gradient-to-br text-yellow-900 bg-linear-to-tl to-yellow-500 from-yellow-400",
      purple: "bg-gradient-to-br text-purple-900 bg-linear-to-tl to-purple-500 from-purple-400",
      orange: "bg-gradient-to-br text-orange-900 bg-linear-to-tl to-orange-500 from-orange-400",
      pink: "bg-gradient-to-br text-pink-900 bg-linear-to-tl to-pink-500 from-pink-400",
      amber: "bg-gradient-to-br text-amber-900 bg-linear-to-tl to-amber-500 from-amber-400",
      rose: "bg-gradient-to-br text-rose-900 bg-linear-to-tl to-rose-500 from-rose-400",
      violet: "bg-gradient-to-br text-violet-900 bg-linear-to-tl to-violet-500 from-violet-400",
      teal: "bg-gradient-to-br text-teal-900 bg-linear-to-tl to-teal-500 from-teal-400",
      gray: "bg-gradient-to-br text-gray-900 bg-linear-to-tl to-gray-500 from-gray-400",
    },
  },
  defaultVariants: {
    color: "blue",
  },
});

// Loop over all leerlingen in a lesgroep
function LesGroep({ lesgroep, params, className, index, plan, ...props }: LesGroepProps) {
  const { amountOfGroups, layoutType, getLesgroepColor } = useLeerlingGestuurdeLes();

  const color = useMemo(() => {
    return getLesgroepColor(index);
  }, [index, getLesgroepColor]);
  // A groepje smaller than one row of the shared grid gets only the columns it fills, so it centers.
  const columns = plan ? Math.min(plan.columns, lesgroep.leerlingen.length) : 0;

  return (
    <motion.article
      className={cn(LesGroepVariants({ color }), "relative flex min-w-0 grow", className)}
      {...props}
    >
      <span
        className="font-heading absolute bottom-2 left-2 text-base leading-none
          font-black opacity-50"
      >
        {lesgroep.groep}
      </span>
      <div
        data-name-grid
        className={cn(
          "relative grid min-h-0 min-w-0 grow gap-6 p-10",
          {
            // Several groups share the screen, so a large les gets the tighter medium spacing there.
            "max-2xl:gap-4 max-2xl:p-5 max-xl:gap-2 max-xl:p-3":
              layoutType === "medium" || (layoutType === "large" && amountOfGroups > 1),
          },
          {
            "max-2xl:gap-3 max-2xl:p-4 max-xl:gap-1 max-xl:p-1": layoutType === "small",
          },
          { "gap-6 p-10": layoutType === "large" },
        )}
        style={
          plan
            ? {
                gridTemplateColumns: `repeat(${columns}, minmax(0, ${plan.buttonWidth}px))`,
                gridAutoRows: `${plan.rowHeight}px`,
                alignContent: "center",
                justifyContent: "center",
              }
            : undefined
        }
      >
        {lesgroep.leerlingen.map((leerling: components["schemas"]["LesgroepLeerlingModel"]) => (
          <Student key={leerling.id} leerling={leerling} params={params} groupIndex={index} />
        ))}
      </div>
    </motion.article>
  );
}

function StudentIndexPage() {
  const params = Route.useParams();
  const { lesson, layoutType } = useLeerlingGestuurdeLes();
  const pageRef = useRef<HTMLDivElement>(null);
  const names = lesson.lesgroepen.flatMap((lesgroep) =>
    lesgroep.leerlingen.map(({ name }) => name),
  );
  const room = usePageRoom(pageRef, "article", names);
  const groups = lesson.lesgroepen.length;
  const plan = room
    ? planNames({
        room,
        groups,
        largestGroup: Math.max(0, ...lesson.lesgroepen.map(({ leerlingen }) => leerlingen.length)),
        names,
        avatars: lesson.avatarsEnabled,
        layoutType,
      })
    : null;

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.05 },
    },
  };

  const item = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1 },
  };

  // On one screen the page is exactly the measured viewport high and every group gets an equal
  // cell; stacked, the groups take the full width and the page scrolls.
  const rows = plan ? Math.ceil(groups / plan.groupColumns) : 1;
  const pageStyle: CSSProperties = {
    gap: GROUP_GAP,
    height: plan && !plan.stacked ? room?.viewport.height : undefined,
    "--name-font": plan ? `${plan.font}px` : undefined,
    "--avatar-size": plan ? `${plan.avatar}px` : undefined,
  };
  // 1px slack keeps rounding from wrapping a row early.
  const cellStyle: CSSProperties | undefined =
    plan && !plan.stacked
      ? {
          flex: `1 1 calc((100% - ${(plan.groupColumns - 1) * GROUP_GAP}px) / ${plan.groupColumns} - 1px)`,
          height: `calc((100% - ${(rows - 1) * GROUP_GAP}px) / ${rows})`,
        }
      : undefined;

  return (
    <motion.div
      ref={pageRef}
      style={pageStyle}
      className={cn(
        "flex grow p-2",
        plan && !plan.stacked ? "flex-wrap content-start" : "flex-col",
      )}
      variants={container}
      initial="hidden"
      animate="show"
    >
      {lesson.lesgroepen.map((lesgroep, index) => (
        <LesGroep
          key={lesgroep.groep}
          lesgroep={lesgroep}
          params={params}
          index={index}
          variants={item}
          plan={plan}
          style={cellStyle}
        />
      ))}
    </motion.div>
  );
}
